import { withNamespaces } from "react-i18next";
import { UncontrolledTooltip } from "reactstrap";

const SummaryCardSkeleton = ({ cardKey }) => {
    return (
        <article
            key={cardKey}
            className="assessment-panel__summary-card"
        >
            <p
                className="dt-field dt-skeleton mb-2"
                style={{ height: "20px", width: "70%" }}
            />
            <p
                className="dt-field dt-skeleton mb-2"
                style={{ height: "28px", width: "40%" }}
            />
            <p
                className="dt-field dt-skeleton mb-0"
                style={{ height: "32px", width: "100%" }}
            />
        </article>
    );
};

/**
 * Copy can be a plain key or a `{ key, values }` pair when the sentence
 * carries counts, so the whole sentence stays a single translatable key.
 */
const translate = (t, copy) => {
    if (copy && typeof copy === "object") {
        if (copy.literal != null) {
            return copy.literal;
        }

        if (Array.isArray(copy.labels)) {
            return copy.labels.map((label) => t(label)).join(", ");
        }

        return t(copy.key, copy.values);
    }

    return t(copy);
};

const formatValue = (t, card) => {
    if (card.value == null || card.value === "") {
        return null;
    }

    if (card.literalValue || typeof card.value === "number") {
        return String(card.value);
    }

    return t(String(card.value));
};

const AssessmentSummaryCards = ({
    t,
    cards = [],
    isLoading = false,
    hidden = false,
    skeletonCount = 3,
    onAction
}) => {
    const containerClassName = `assessment-panel__summaries ${
        hidden ? "d-none" : ""
    }`.trim();

    if (isLoading) {
        return (
            <div className={containerClassName}>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <SummaryCardSkeleton
                        key={index}
                        cardKey={String(index)}
                    />
                ))}
            </div>
        );
    }

    if (!cards.length && !hidden) {
        return null;
    }

    return (
        <div className={containerClassName}>
            {cards.map((card) => {
                const value = formatValue(t, card);
                const infoTarget = `assessment-summary-info-${card.key}`;

                return (
                    <article
                        key={card.key}
                        className="assessment-panel__summary-card"
                    >
                        <div className="assessment-panel__summary-card-head">
                            <span
                                className={`assessment-panel__summary-icon ${
                                    card.tone || ""
                                }`}
                            >
                                <i className={card.icon} aria-hidden="true" />
                            </span>
                            <strong>{t(card.title)}</strong>
                            {card.info && (
                                <>
                                    <button
                                        id={infoTarget}
                                        type="button"
                                        className="assessment-panel__summary-info"
                                        aria-label={t("About {{title}}", {
                                            title: t(card.title)
                                        })}
                                    >
                                        <i
                                            className="ri-information-line"
                                            aria-hidden="true"
                                        />
                                    </button>
                                    <UncontrolledTooltip
                                        target={infoTarget}
                                        placement="top"
                                    >
                                        {t(card.info)}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {card.badge && (
                                <span
                                    className={`assessment-panel__summary-badge ${
                                        card.badgeTone || ""
                                    }`}
                                >
                                    {t(card.badge)}
                                </span>
                            )}
                        </div>

                        {value != null && (
                            <p
                                className={`assessment-panel__summary-value ${
                                    card.valueTone || ""
                                } ${card.valueSize || ""}`}
                            >
                                <span>{value}</span>
                                {card.valueLabel && (
                                    <small>{t(card.valueLabel)}</small>
                                )}
                            </p>
                        )}

                        {card.meta && (
                            <p className="assessment-panel__summary-meta">
                                <i
                                    className="ri-file-list-3-line"
                                    aria-hidden="true"
                                />
                                {t(card.meta)}
                            </p>
                        )}

                        {card.description ? (
                            <p className="assessment-panel__summary-copy">
                                {translate(t, card.description)}
                            </p>
                        ) : null}

                        {card.actionLabel && (
                            <button
                                type="button"
                                className="assessment-panel__summary-link"
                                onClick={
                                    onAction
                                        ? () => onAction(card)
                                        : undefined
                                }
                            >
                                {t(card.actionLabel)}
                                <i
                                    className="ri-arrow-right-line"
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                    </article>
                );
            })}
        </div>
    );
};

export default withNamespaces()(AssessmentSummaryCards);
